// @ts-nocheck
import React, { useState } from 'react';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { ShopKeyPrompt, API_URL, BUSINESS_CATEGORIES, COUNTRIES } from './types';

interface Props {
  shopKeyPrompt?: ShopKeyPrompt | null;
  // Callback mode (used by OnboardPage)
  isOpen?: boolean;
  username?: string;
  onShopLinked?: (shopId: string) => void;
  onCancel?: () => void;
}

const ShopKeyPromptModal: React.FC<Props> = ({ shopKeyPrompt, isOpen, username: propUsername, onShopLinked, onCancel }) => {
  const isCallbackMode = !!onShopLinked;
  const [promptShopId, setPromptShopId] = useState('');
  const [shopPromptMode, setShopPromptMode] = useState<'link' | 'create'>('link');
  const [createShopData, setCreateShopData] = useState({
    businessName: '',
    country: COUNTRIES[0],
    category: BUSINESS_CATEGORIES[0],
    phone: ''
  });
  const [createShopLoading, setCreateShopLoading] = useState(false);
  const [createShopError, setCreateShopError] = useState<string | null>(null);

  const handleShopKeySubmit = async () => {
    if (!promptShopId) return;
    if (isCallbackMode) {
      // In callback mode, set igSync on the linked shop then call back
      const igUsername = propUsername || '';
      try {
        await updateDoc(doc(db, 'shops', promptShopId), {
          igSync: {
            enabled: true,
            instagramUrl: igUsername ? `https://www.instagram.com/${igUsername}/` : '',
            isSyncing: false,
            lastSync: null
          },
          generalMessage: 'Production \ud835\udc2d\ud835\udc1a\ud835\udc24\ud835\udc1e\ud835\udc2c 5 - 10 \ud835\udc30\ud835\udc28\ud835\udc2b\ud835\udc24\ud835\udc22\ud835\udc27\ud835\udc20 \ud835\udc1d\ud835\udc1a\ud835\udc32\ud835\udc2c',
          isIndexible: true,
          isVisibleOnMarketplace: true
        });
      } catch (e) { console.error('Failed to update shop igSync:', e); }
      onShopLinked(promptShopId);
      setPromptShopId('');
      return;
    }
    const prompt = (window as any).__shopKeyPrompt;
    if (!prompt) return;
    prompt.shopId = promptShopId;
    prompt.resolved = true;
    prompt.cancelled = false;
    setPromptShopId('');
  };

  const handleShopKeyCancel = () => {
    if (isCallbackMode) {
      onCancel?.();
      setPromptShopId('');
      return;
    }
    const prompt = (window as any).__shopKeyPrompt;
    if (!prompt) return;
    prompt.shopId = null;
    prompt.resolved = true;
    prompt.cancelled = true;
    setPromptShopId('');
  };

  const ensurePrivateCollectionExists = async (shopId: string, shopData: any) => {
    const privateShopRef = doc(db, 'shops', shopId, 'private', 'shop');
    const privateShopSnap = await getDoc(privateShopRef);

    if (!privateShopSnap.exists()) {
      const privateData = {
        businessName: shopData.businessName,
        subscription: {},
        merchant: shopData.merchant,
        ...shopData,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      delete privateData.stats;
      delete privateData.shopSync;
      await setDoc(privateShopRef, privateData);
    }
  };

  const handleCreateShop = async () => {
    if (!createShopData.businessName || !createShopData.phone) return;
    const prompt = isCallbackMode ? null : (window as any).__shopKeyPrompt;
    if (!isCallbackMode && !prompt) return;

    setCreateShopLoading(true);
    setCreateShopError(null);

    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Not authenticated');

      const res = await fetch(`${API_URL}/auth/createShop`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token,
          '$shopkey': ''
        },
        body: JSON.stringify({
          businessName: createShopData.businessName,
          country: createShopData.country,
          category: createShopData.category,
          phone: createShopData.phone
        })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.error || errData?.message || 'Failed to create shop');
      }

      const data = await res.json();
      if (data.success && data.shopId) {
        await ensurePrivateCollectionExists(data.shopId, {
          businessName: createShopData.businessName,
          country: createShopData.country,
          category: createShopData.category,
          merchant: {
            phone: createShopData.phone,
            email: auth.currentUser?.email || '',
            uid: auth.currentUser?.uid || ''
          }
        });

        const igUsername = propUsername || shopKeyPrompt?.username || '';
        await updateDoc(doc(db, 'shops', data.shopId), {
          igSync: {
            enabled: true,
            instagramUrl: igUsername ? `https://www.instagram.com/${igUsername}/` : '',
            isSyncing: false,
            lastSync: null
          },
          generalMessage: 'Production \ud835\udc2d\ud835\udc1a\ud835\udc24\ud835\udc1e\ud835\udc2c 5 - 10 \ud835\udc30\ud835\udc28\ud835\udc2b\ud835\udc24\ud835\udc22\ud835\udc27\ud835\udc20 \ud835\udc1d\ud835\udc1a\ud835\udc32\ud835\udc2c',
          isIndexible: true,
          isVisibleOnMarketplace: true
        });

        if (isCallbackMode) {
          onShopLinked(data.shopId);
        } else if (prompt) {
          prompt.shopId = data.shopId;
          prompt.resolved = true;
          prompt.cancelled = false;
        }
        setCreateShopData({ businessName: '', country: COUNTRIES[0], category: BUSINESS_CATEGORIES[0], phone: '' });
        setShopPromptMode('link');
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (error: any) {
      console.error('Create shop error:', error);
      setCreateShopError(error.message || 'Failed to create shop');
    } finally {
      setCreateShopLoading(false);
    }
  };

  // Determine visibility
  const isVisible = isCallbackMode ? !!isOpen : (shopKeyPrompt?.pending && !shopKeyPrompt?.resolved);
  if (!isVisible) return null;

  const displayUsername = propUsername || shopKeyPrompt?.username || '';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4">
          <h3 className="text-lg font-bold text-white">
            {shopPromptMode === 'link' ? 'Shop Key Required' : 'Create New Shop'}
          </h3>
          <p className="text-sm text-white/80">
            {shopPromptMode === 'link'
              ? 'Link this Instagram account to a shop'
              : 'Create a new shop for this Instagram account'}
          </p>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-xs uppercase text-gray-500 font-bold mb-1">Instagram Account</p>
            <p className="text-xl font-bold text-gray-900">@{displayUsername}</p>
          </div>

          {/* Mode Toggle */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => setShopPromptMode('link')}
              className={`flex-1 py-2 text-sm font-bold transition-colors ${shopPromptMode === 'link'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
            >
              Link Existing
            </button>
            <button
              onClick={() => {
                setShopPromptMode('create');
                setCreateShopError(null);
                if (!createShopData.businessName && displayUsername) {
                  setCreateShopData(prev => ({ ...prev, businessName: displayUsername }));
                }
              }}
              className={`flex-1 py-2 text-sm font-bold transition-colors ${shopPromptMode === 'create'
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
            >
              Create New
            </button>
          </div>

          {shopPromptMode === 'link' ? (
            <>
              <p className="text-sm text-gray-600">
                No shop is linked to this Instagram account. Enter the Shop ID to associate with this profile for future imports.
              </p>
              <input
                type="text"
                placeholder="Enter Shop ID (e.g., abc123)"
                value={promptShopId}
                onChange={(e) => setPromptShopId(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleShopKeySubmit()}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                autoFocus
              />
              <div className="flex space-x-3">
                <button
                  onClick={handleShopKeyCancel}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleShopKeySubmit}
                  disabled={!promptShopId}
                  className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg text-sm font-bold hover:bg-purple-700 disabled:bg-gray-200 disabled:text-gray-400 transition-colors"
                >
                  Link Shop
                </button>
              </div>
            </>
          ) : (
            <>
              {createShopError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {createShopError}
                </div>
              )}
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Business Name</label>
                <input
                  type="text"
                  placeholder="e.g. Stylish Boutique"
                  value={createShopData.businessName}
                  onChange={(e) => setCreateShopData(prev => ({ ...prev, businessName: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Phone Number</label>
                <input
                  type="tel"
                  placeholder="e.g. +234 800 000 0000"
                  value={createShopData.phone}
                  onChange={(e) => setCreateShopData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Country</label>
                  <select
                    value={createShopData.country.value}
                    onChange={(e) => {
                      const c = COUNTRIES.find(c => c.value === e.target.value);
                      if (c) setCreateShopData(prev => ({ ...prev, country: c }));
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                  >
                    {COUNTRIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Category</label>
                  <select
                    value={createShopData.category.value}
                    onChange={(e) => {
                      const cat = BUSINESS_CATEGORIES.find(c => c.value === e.target.value);
                      if (cat) setCreateShopData(prev => ({ ...prev, category: cat }));
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                  >
                    {BUSINESS_CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleShopKeyCancel}
                  disabled={createShopLoading}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateShop}
                  disabled={createShopLoading || !createShopData.businessName || !createShopData.phone}
                  className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg text-sm font-bold hover:bg-green-700 disabled:bg-gray-200 disabled:text-gray-400 transition-colors flex items-center justify-center gap-2"
                >
                  {createShopLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <span>Create Shop</span>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopKeyPromptModal;
